<template>
  <div class="card" v-if="!loading">
    <div class="card-header bg-white">
      <h5><i class="fa-solid fa-percent me-2"></i>GST/HST Registration</h5>
    </div>
    <div class="card-body">
      <div v-if="summary.gstHstNumber" class="alert alert-success mb-0">
        <i class="fa fa-check-circle me-2"></i>On file: <strong>{{ summary.gstHstNumber }}</strong>
      </div>

      <!-- A failed read must never render as a statement of fact about a compliance record: an admin
           who reads "nothing on file" when the request merely failed to load will tell the driver to
           re-submit, creating a duplicate authorization and a second live SIN. -->
      <div v-else-if="loadError" class="alert alert-danger mb-0">
        <i class="fa fa-exclamation-triangle me-2"></i>
        {{ loadError }}
        <button class="btn btn-sm btn-outline-danger ms-2" @click="fetchSummary">Retry</button>
      </div>

      <div v-else-if="!summary.openRequest">
        <div class="alert alert-warning">
          <i class="fa fa-exclamation-triangle me-2"></i>
          This driver has no GST/HST number and no registration request on file.
        </div>
        <div class="row g-2 align-items-end">
          <div class="col-md-6">
            <label class="form-label f-w-600" for="admin-tax-direct-number-input">Record GST/HST Number</label>
            <input id="admin-tax-direct-number-input" class="form-control" type="text"
                   v-model="directNumber" placeholder="e.g., 123456789RT0001">
          </div>
          <div class="col-md-6">
            <button class="btn btn-success" @click="setNumberDirectly"
                    :disabled="settingDirect || !directNumber.trim()">
              <span v-if="settingDirect" class="spinner-border spinner-border-sm me-1"></span>
              {{ settingDirect ? 'Saving...' : 'Save Number' }}
            </button>
          </div>
        </div>
        <div v-if="actionError" class="alert alert-danger mt-3 mb-0">{{ actionError }}</div>
      </div>

      <div v-else>
        <span class="badge bg-warning mb-3">{{ summary.openRequest.status.replace(/_/g, ' ') }}</span>

        <dl class="row mb-3">
          <dt class="col-sm-4">Legal Name</dt>
          <dd class="col-sm-8">{{ summary.openRequest.legalName }}</dd>

          <dt class="col-sm-4">SIN</dt>
          <dd class="col-sm-8">
            <span v-if="revealedSin">{{ revealedSin }}</span>
            <span v-else>{{ summary.openRequest.sinMasked }}</span>
            <button class="btn btn-sm btn-outline-secondary ms-2" @click="reveal" :disabled="revealing || !!revealedSin">
              <span v-if="revealing" class="spinner-border spinner-border-sm"></span>
              <span v-else-if="revealedSin"><i class="fa fa-check me-1"></i>Revealed</span>
              <span v-else><i class="fa fa-eye me-1"></i>Reveal</span>
            </button>
            <div v-if="revealedSin" class="form-text">This access has been logged.</div>
          </dd>

          <dt class="col-sm-4">Address</dt>
          <dd class="col-sm-8">
            {{ summary.openRequest.address }}, {{ summary.openRequest.city }}, {{ summary.openRequest.state }}
            {{ summary.openRequest.postalCode }}, {{ summary.openRequest.country }}
          </dd>

          <dt class="col-sm-4">Business Activity</dt>
          <dd class="col-sm-8">{{ summary.openRequest.businessActivity }}</dd>

          <!-- Drives which CRA form to file; the process itself is identical either way. -->
          <dt class="col-sm-4">Canadian Tax Resident</dt>
          <dd class="col-sm-8">
            <span v-if="summary.openRequest.canadianTaxResident === true" class="badge bg-success">
              Yes — resident form
            </span>
            <span v-else-if="summary.openRequest.canadianTaxResident === false" class="badge bg-info">
              No — non-resident form
            </span>
            <span v-else class="badge bg-secondary">Not answered</span>
          </dd>

          <dt class="col-sm-4">Registration Start Date</dt>
          <dd class="col-sm-8">{{ summary.openRequest.registrationStartDate }}</dd>

          <dt class="col-sm-4">Authorized On</dt>
          <dd class="col-sm-8">{{ formatDate(summary.openRequest.authorizedOn) }}</dd>
        </dl>

        <div class="row g-2 align-items-end">
          <div class="col-md-6">
            <label class="form-label f-w-600" for="admin-tax-resolve-number-input">Resulting GST/HST Number</label>
            <input id="admin-tax-resolve-number-input" class="form-control" type="text"
                   v-model="resolveNumber" placeholder="e.g., 123456789RT0001">
          </div>
          <div class="col-md-12">
            <label class="form-label f-w-600" for="admin-tax-reject-reason-input">Rejection Reason (shown to the driver)</label>
            <input id="admin-tax-reject-reason-input" class="form-control" type="text"
                   v-model="rejectReason" placeholder="Optional, but saves the driver guessing">
          </div>
          <div class="col-md-6 d-flex gap-2">
            <button class="btn btn-success" @click="resolve" :disabled="resolving || !resolveNumber.trim()">
              <span v-if="resolving" class="spinner-border spinner-border-sm me-1"></span>
              {{ resolving ? 'Resolving...' : 'Resolve' }}
            </button>
            <button class="btn btn-outline-danger" @click="reject" :disabled="rejecting">
              <span v-if="rejecting" class="spinner-border spinner-border-sm me-1"></span>
              {{ rejecting ? 'Rejecting...' : 'Reject' }}
            </button>
          </div>
        </div>
        <div v-if="actionError" class="alert alert-danger mt-3 mb-0">{{ actionError }}</div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue';
import moment from 'moment/moment';

const props = defineProps({
  driverId: { type: String, required: true },
});

const emit = defineEmits(['resolved']);

const loading = ref(true);
const loadError = ref(null);
const summary = reactive({ gstHstNumber: null, openRequest: null });

const revealedSin = ref('');
const revealing = ref(false);

const resolveNumber = ref('');
const rejectReason = ref('');
const directNumber = ref('');
const settingDirect = ref(false);
const resolving = ref(false);
const rejecting = ref(false);
const actionError = ref(null);

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    return moment(dateStr).format('MMMM Do, YYYY [at] h:mm a');
  } catch {
    return dateStr;
  }
};

const fetchSummary = async () => {
  loading.value = true;
  loadError.value = null;
  revealedSin.value = '';
  try {
    const response = await fetch(`/api/admin/driver-tax-registration/by-driver/${props.driverId}`);
    if (!response.ok) {
      throw new Error('Unable to load tax registration summary.');
    }
    const data = await response.json();
    summary.gstHstNumber = data.gstHstNumber;
    summary.openRequest = data.openRequest;
  } catch (e) {
    // Must not fall through to the "nothing on file" branch — see the template comment.
    loadError.value = 'Unable to load this driver\'s GST/HST registration.';
  } finally {
    loading.value = false;
  }
};

/** Records a number for a driver who never submitted a registration request (issue #252). */
const setNumberDirectly = async () => {
  settingDirect.value = true;
  actionError.value = null;
  try {
    const response = await fetch(`/api/admin/driver-tax-registration/by-driver/${props.driverId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ gstHstNumber: directNumber.value }),
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || 'Unable to record the GST/HST number.');
    }
    directNumber.value = '';
    await fetchSummary();
    emit('resolved');
  } catch (e) {
    actionError.value = e.message || 'Unable to record the GST/HST number.';
  } finally {
    settingDirect.value = false;
  }
};

const reveal = async () => {
  if (!summary.openRequest) return;
  revealing.value = true;
  actionError.value = null;
  try {
    const response = await fetch(`/api/admin/driver-tax-registration/${summary.openRequest.id}/reveal-sin`, {
      method: 'POST',
    });
    if (!response.ok) {
      throw new Error('Unable to reveal the SIN.');
    }
    const data = await response.json();
    revealedSin.value = data.sin;
  } catch (e) {
    actionError.value = e.message || 'Unable to reveal the SIN.';
  } finally {
    revealing.value = false;
  }
};

const resolve = async () => {
  if (!summary.openRequest) return;
  resolving.value = true;
  actionError.value = null;
  try {
    const response = await fetch(`/api/admin/driver-tax-registration/${summary.openRequest.id}/resolve`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ gstHstNumber: resolveNumber.value }),
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || 'Unable to resolve the request.');
    }
    resolveNumber.value = '';
    await fetchSummary();
    emit('resolved');
  } catch (e) {
    actionError.value = e.message || 'Unable to resolve the request.';
  } finally {
    resolving.value = false;
  }
};

const reject = async () => {
  if (!summary.openRequest) return;
  rejecting.value = true;
  actionError.value = null;
  try {
    const response = await fetch(`/api/admin/driver-tax-registration/${summary.openRequest.id}/reject`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason: rejectReason.value || null }),
    });
    if (!response.ok) {
      throw new Error('Unable to reject the request.');
    }
    await fetchSummary();
    emit('resolved');
  } catch (e) {
    actionError.value = e.message || 'Unable to reject the request.';
  } finally {
    rejecting.value = false;
  }
};

onMounted(fetchSummary);
</script>

<style scoped>
</style>
